import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Checkbox,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  InputAdornment,
  IconButton,
  CircularProgress,
  LinearProgress,
  Tooltip,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel,
  FormControlLabel
} from "@mui/material";
import LOGO_BASE64 from "../../assets/data/logoBase64";
import { FaEdit } from "react-icons/fa";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useAuth } from "../../context/AuthContext";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PrintIcon from "@mui/icons-material/Print";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { FaTrash } from "react-icons/fa";
import RestoreIcon from "@mui/icons-material/Restore";
import JSZip from "jszip";
import html2pdf from "html2pdf.js";
import RangeFilter from "../../components/RangeFilter";
import ConfirmDialog from "../../components/ConfirmDialog";

// Yup Validation Schema
const schema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters"),
  mobile: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{10}$/, "Enter a valid 10-digit number"),
  location: Yup.string().required("Location is required"),
  branch: Yup.string().required('Please select a branch')
}).required();

const pulseAnimation = {
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.7)',
    },
    '70%': {
      boxShadow: '0 0 0 6px rgba(239, 68, 68, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)',
    },
  },
};

function ListPage({ isDeletedPage = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [customersList, setCustomersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editCustomer, setEditCustomer] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [customerId, setCustomerId] = useState("");
  const [branches, setBranches] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [openTrayDialog, setOpenTrayDialog] = useState(false);
  const [trayTypes, setTrayTypes] = useState([]);
  const [selectedCustomerTrayId, setSelectedCustomerTrayId] = useState("");
  const [trayTypeId, setTrayTypeId] = useState("");
  const [trayCount, setTrayCount] = useState("");
  const [savingTray, setSavingTray] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [statusFilter, setStatusFilter] = useState(isDeletedPage ? "deleted" : "all");
  const reportRanges = [
    {
      label: 'This Week',
      value: [dayjs().startOf('week').toDate(), dayjs().toDate()],
    },
    {
      label: 'Last Week',
      value: [
        dayjs().subtract(1, 'week').startOf('week').toDate(),
        dayjs().subtract(1, 'week').endOf('week').toDate(),
      ],
    },
    {
      label: 'This Month',
      value: [dayjs().startOf('month').toDate(), dayjs().toDate()],
    },
    {
      label: 'Last Month',
      value: [
        dayjs().subtract(1, 'month').startOf('month').toDate(),
        dayjs().subtract(1, 'month').endOf('month').toDate(),
      ],
    },
  ];
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);

  const { user } = useAuth();

  const searchString = searchParams.get("search") || "";
  const filteredBranch = searchParams.get("branch") || "";

  const [searchValue, setSearchValue] = useState(searchString);

  // Sync URL search string to local state (for back/forward navigation or initial load)
  useEffect(() => {
    setSearchValue(searchString);
  }, [searchString]);

  // Debounce updating the searchParams
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchValue !== searchString) {
        setSearchParams((prev) => {
          if (searchValue) {
            prev.set("search", searchValue);
          } else {
            prev.delete("search");
          }
          prev.set("page", "0");
          return prev;
        });
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [searchValue, setSearchParams, searchString]);

  // Fetch Customers on Mount
  useEffect(() => {
    // getCustomersList();
    getBranchList()
    if(user?.roleId === 1){
      getEmployersList()
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0)
      getCustomersList();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchString, filteredBranch, selectedDate, statusFilter]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0)
      getBranchList()
      getTrayTypes();
    }, 500);

    return () => clearTimeout(timer);
  }, [selectedEmployerId]);

  useEffect(() => {
    setStatusFilter(isDeletedPage ? "deleted" : "all");
    setSelectedDate("");
  }, [isDeletedPage]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleBranchChange = (branchId) => {
    setSearchParams((prev) => {
      prev.set("branch", branchId);
      prev.set("page", "0");
      return prev;
    });
  };



  const getTrayTypes = async () => {
    try {
      if(!selectedEmployerId)return;
      const res = await axiosInstance.get("/trays?employerId="+ selectedEmployerId);
      setTrayTypes(res.data.list || []);
    } catch {
      toast.error("Failed to load tray types");
    }
  };

  const openTrayPopup = (customer) => {
    setSelectedCustomerTrayId(customer._id);
    setTrayTypeId("");
    setTrayCount("");
    setOpenTrayDialog(true);
  };

  const handleUpdateTray = async () => {
    try {
      setSavingTray(true);

      await axiosInstance.put(
        `/customers/${selectedCustomerTrayId}/tray-balance`,
        {
          trays: [
            {
              trayTypeId: trayTypeId,
              count: Number(trayCount)
            }
          ]
        }
      );

      toast.success("Tray balance updated");
      setOpenTrayDialog(false);

    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update tray");
    } finally {
      setSavingTray(false);
    }
  };

  const getEmployersList = async () => {
    try {
      const res = await axiosInstance.get("/employers");
      setEmployers(res.data);
      if(res.data.length > 0){
        setSelectedEmployerId(res.data[0]['_id'])
      }
    } catch {
      console.log('Error')
    }
  }

  const getBranchList = async () => {
    try {
      const response = await axiosInstance.get('/branches?employerId='+ selectedEmployerId)
      const list = response?.data?.list || [];
      setBranches(list);

      if (list.length > 0 && !searchParams.get("branch")) {
        handleBranchChange(list[0]._id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error retrieving list')
    }
  }

  // Fetch Customers
  const getCustomersList = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    try {
      let url = `/customers/list?branchId=${filteredBranch}&searchString=${searchString}&employerId=${selectedEmployerId}`;
      if (selectedDate) {
        url += `&endDate=${selectedDate}`;
      }
      if (statusFilter === "deleted") {
        url += `&status=deleted`;
      }
      const response = await axiosInstance.get(url);
      setCustomersList(response.data.result || []);
    } catch (err) {
      if (err.name === "CanceledError" || err.__CANCEL__) {
        return; 
      }
      toast.error(err.response?.data?.message || "Failed to load customers");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  // Close Popup
  const handleCreateCustomerPopupClose = () => {
    setOpen(false);
    setCustomerId('')
    reset({
      name: '',
      mobile: '',
      location: '',
      branch: ''
    })
    setEditCustomer(false)
  };

  // Submit New Customer
  const handleCreateCustomerSubmit = async (data) => {
    try {
      setSaving(true);
      let path = "/customers/create"
      const obj = {
        location: data?.location,
        mobile: data?.mobile,
        name: data?.name,
        branchId: data?.branch,
      }
      if(user?.roleId === 1){
        obj.employerId = selectedEmployerId
      }
      if(editCustomer){
        path = '/customers/update'
        obj.customerId = customerId
      }
      const response = await axiosInstance.post(path, obj);
      toast.success(response.data.message || "Customer saved successfully");
      handleCreateCustomerPopupClose();
      getCustomersList();
      setSaving(false);
      setEditCustomer(false)
    } catch (err) {
      setSaving(false);
      toast.error(err.response?.data?.message || "Failed to add customer");
    }
  };

  // Delete Customer
  const handleDeleteCustomer = async (customer) => {
    if(customer.totalDueAmount > 0){
      toast.error("The customer’s outstanding balance must be fully settled before the customer can be deleted.");
      return;
    }
    setCustomerToDelete(customer._id);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteCustomer = async () => {
    try {
      const response = await axiosInstance.delete(`/customers/${customerToDelete}`);
      toast.success(response.data.message || "Customer deleted successfully");
      getCustomersList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete customer");
    } finally {
      setOpenDeleteDialog(false);
      setCustomerToDelete(null);
    }
  };

  const cancelDeleteCustomer = () => {
    setOpenDeleteDialog(false);
    setCustomerToDelete(null);
  };

  const handleRestoreCustomer = async (customerId) => {
    try {
      const response = await axiosInstance.put(`/customers/${customerId}/restore`);
      toast.success(response.data.message || "Customer restored successfully");
      getCustomersList();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to restore customer");
    }
  };

  const filteredCustomers = (() => {
    const list = customersList.filter((customer) => {
      const referenceDate = selectedDate ? dayjs(selectedDate) : dayjs();
      const isInactive = customer.lastTransactionDate
        ? referenceDate.diff(dayjs(customer.lastTransactionDate), "day") > 30
        : true;

      if (statusFilter === "all") return true;
      if (statusFilter === "inactive") return isInactive;
      if (statusFilter === "deleted") return true;
      return true;
    });

    if (statusFilter === "inactive") {
      list.sort((a, b) => {
        if (!a.lastTransactionDate && !b.lastTransactionDate) return 0;
        if (!a.lastTransactionDate) return -1;
        if (!b.lastTransactionDate) return 1;

        const referenceDate = selectedDate ? dayjs(selectedDate) : dayjs();
        const daysA = referenceDate.diff(dayjs(a.lastTransactionDate), "day");
        const daysB = referenceDate.diff(dayjs(b.lastTransactionDate), "day");
        return daysB - daysA;
      });
    }

    return list;
  })();

  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

//   const handleSelectAllCustomers = (event) => {
//     const pageCustomerIds = paginatedCustomers.map((customer) => customer._id);
//     if (event.target.checked) {
//       setSelectedCustomerIds((prev) => Array.from(new Set([...prev, ...pageCustomerIds])));
//     } else {
//       setSelectedCustomerIds((prev) => prev.filter((id) => !pageCustomerIds.includes(id)));
//     }
//   };

//   const handleSelectCustomer = (event, customerId) => {
//     if (event.target.checked) {
//       setSelectedCustomerIds((prev) => [...prev, customerId]);
//     } else {
//       setSelectedCustomerIds((prev) => prev.filter((id) => id !== customerId));
//     }
//   };

//   const isAllPageSelected =
//     paginatedCustomers.length > 0 &&
//     paginatedCustomers.every((customer) => selectedCustomerIds.includes(customer._id));

//   const sanitizeFileName = (name) =>
//     (name || "customer")
//       .replace(/[\\/:*?"<>|]/g, "")
//       .trim()
//       .replace(/\s+/g, " ");

//   const formatDate = (value) =>
//     value
//       ? new Date(value).toLocaleDateString("en-IN", {
//           day: "2-digit",
//           month: "short",
//           year: "numeric",
//         })
//       : "-";

//   const formatCurrency = (value) =>
//     `₹${Number(value || 0).toLocaleString("en-IN")}`;

//   const createCustomerReportHtml = (customer, transactions, dateRange) => {
//   const val = (v) => v ?? "-";
//   const reportPeriodLabel =
//     dateRange?.startDate && dateRange?.endDate
//       ? `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`
//       : "All time";

//   const grandTotalTransactionAmount = (transactions || []).reduce((sum, tx) => {
//     const salesTotal = (tx.sales || []).reduce((tSum, sale) => tSum + Number(sale?.total || 0), 0);
//     return sum + salesTotal;
//   }, 0);

//   const discountAmount = (transactions || []).reduce((sum, tx) => sum + Number(tx?.discount || 0), 0);

//   const damageRefund = (transactions || []).reduce((sum, tx) => {
//     const damageTotal = (tx.damageRefunds || [tx.damageRefund] || []).reduce((tSum, damage) => tSum + Number(damage?.total || 0), 0);
//     return sum + damageTotal;
//   }, 0);

//   const labourCharges = (transactions || []).reduce((sum, tx) => sum + Number(tx?.labourCharges?.total || 0), 0);

//   const grandTotalPaidAmount = (transactions || []).reduce((sum, tx) => sum + Number(tx?.paymentAmount || 0), 0);

//   const grandTrayPaymentAmount = (transactions || []).reduce((sum, tx) => sum + (tx.trays || []).reduce((tSum, tray) => tSum + Number(tray?.numberOfTrays || 0) * Number(tray?.costPerTray || 0), 0), 0);

//   return `
//     <!DOCTYPE html>
//     <html>
//       <head>
//         <meta charset="UTF-8" />
//         <title>Transactions - ${customer.name || ""}</title>
//         <style>
//           html, body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; color: #333; background: #fff; }
//           body { margin: 20mm; }
//           * { box-sizing: border-box; }
//           @page { size: A4; margin: 10mm; }
//         </style>
//       </head>
//       <body>
//         <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #1976d2; padding-bottom: 12px; margin-bottom: 18px;">
//           <div>
//             <div style="font-size: 26px; font-weight: 700; color: #1976d2; margin: 0;">${user?.employerName || ""}</div>
//             <div style="color: #666; font-size: 14px; margin-top: 4px;">${customer.branchName || "Transaction Receipt"}</div>
//           </div>
//           <div style="text-align: right;">
//             <div style="font-size: 20px; font-weight: 700; text-transform: capitalize; margin: 0;">${customer.name || ""}</div>
//             <div style="color: #666; font-size: 14px; margin-top: 4px;">${customer.mobile || ""} • ${customer.location || ""}</div>
//           </div>
//         </div>

//         <div style="margin-bottom: 16px; color: #444; font-size: 14px;"> <strong>Period:</strong> ${reportPeriodLabel} </div>

//         <div style="display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;margin-bottom:20px;background:#f5f5f5;padding:15px;border-radius:8px;">
//           <div style="text-align:center;padding:6px 4px;"><div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:4px;">Total Transaction</div><div style="font-size:14px;font-weight:700;">${formatCurrency(grandTotalTransactionAmount)}</div></div>
//           <div style="text-align:center;padding:6px 4px;"><div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:4px;">Total Discount</div><div style="font-size:14px;font-weight:700;">${formatCurrency(discountAmount)}</div></div>
//           <div style="text-align:center;padding:6px 4px;"><div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:4px;">Total Damage Refund</div><div style="font-size:14px;font-weight:700;color:#d32f2f;">${formatCurrency(damageRefund)}</div></div>
//           <div style="text-align:center;padding:6px 4px;"><div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:4px;">Total Amount Paid</div><div style="font-size:14px;font-weight:700;color:#2e7d32;">${formatCurrency(grandTotalPaidAmount + grandTrayPaymentAmount)}</div></div>
//           <div style="text-align:center;padding:6px 4px;"><div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:0.04em;margin-bottom:4px;">Total Amount Due</div><div style="font-size:14px;font-weight:700;color:#1976d2;">${formatCurrency(grandTotalTransactionAmount + labourCharges - (grandTotalPaidAmount + grandTrayPaymentAmount + discountAmount + damageRefund))}</div></div>
//         </div>

//         <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;">
//           <thead>
//             <tr>
//               <th rowspan="2" style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Date</th>
//               <th colspan="5" style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Sales</th>
//               <th colspan="3" style="background:#e4ffe4;border:1px solid #ccc;padding:8px;">Trays Returned</th>
//               <th rowspan="2" style="background:#fff9e6;border:1px solid #ccc;padding:8px;">Discount</th>
//               <th colspan="3" style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Payment</th>
//               <th rowspan="2" style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">By</th>
//             </tr>
//             <tr>
//               <th style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Item</th><th style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Qty</th><th style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Price</th><th style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Total</th><th style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Loading</th>
//               <th style="background:#e4ffe4;border:1px solid #ccc;padding:8px;">Qty</th><th style="background:#e4ffe4;border:1px solid #ccc;padding:8px;">Price</th><th style="background:#e4ffe4;border:1px solid #ccc;padding:8px;">Total</th>
//               <th style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Cash</th><th style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Tray</th><th style="background:#f7f7f7;border:1px solid #ccc;padding:8px;">Total</th>
//             </tr>
//           </thead>
//           <tbody>
//             ${
//               (transactions || []).length === 0
//                 ? `<tr><td colspan="14" style="padding: 20px; text-align: center; color: #666;">No transactions found for this date range.</td></tr>`
//                 : (transactions || []).map((tx) => {
//                     let salesRows = tx.sales ? [...tx.sales] : [];
//                     if (tx?.damageRefunds?.length > 0) {
//                       const tempDamage = tx.damageRefunds.map((d) => ({ ...d, isDamage: true }));
//                       salesRows = [...salesRows, ...tempDamage];
//                     }

//                     const trays = tx.trays || [];
//                     const rowCount = Math.max(salesRows.length, trays.length, 1);
//                     const pAmt = Number(tx.paymentAmount || 0);
//                     const tTotal = trays.reduce((s, t) => s + Number(t.numberOfTrays || 0) * Number(t.costPerTray || 0), 0);

//                     return Array.from({ length: rowCount }).map((_, i) => {
//                       const sale = salesRows[i];
//                       const tray = trays[i];
//                       return `
//                         <tr>
//                           ${i === 0 ? `<td rowspan="${rowCount}" style="border:1px solid #ccc;padding:8px;">${tx.dateOfTransaction ? formatDate(tx.dateOfTransaction) : "-"}</td>` : ""}
//                           <td style="border:1px solid #ccc;padding:8px;${sale?.isDamage ? 'background:#d32f2f;color:#fff;font-weight:700;' : ''}">${val(sale?.itemName)}</td>
//                           <td style="border:1px solid #ccc;padding:8px;${sale?.isDamage ? 'background:#d32f2f;color:#fff;font-weight:700;' : ''}">${val(sale?.numberOfItems)}</td>
//                           <td style="border:1px solid #ccc;padding:8px;${sale?.isDamage ? 'background:#d32f2f;color:#fff;font-weight:700;' : ''}">${val(sale?.costPerItem)}</td>
//                           <td style="border:1px solid #ccc;padding:8px;${sale?.isDamage ? 'background:#d32f2f;color:#fff;font-weight:700;' : ''}">${sale?.isDamage ? '-' : ''}${val(sale?.total)}</td>
//                           ${i === 0 ? `<td rowspan="${rowCount}" style="border:1px solid #ccc;padding:8px;">${val(tx.labourCharges?.total)}</td>` : ""}
//                           <td style="border:1px solid #ccc;padding:8px;background:#e4ffe4;">${val(tray?.numberOfTrays)}</td>
//                           <td style="border:1px solid #ccc;padding:8px;background:#e4ffe4;">${val(tray?.costPerTray)}</td>
//                           <td style="border:1px solid #ccc;padding:8px;background:#e4ffe4;">${tray ? (tray.numberOfTrays * tray.costPerTray) : "-"}</td>
//                           ${i === 0 ? `<td rowspan="${rowCount}" style="border:1px solid #ccc;padding:8px;background:#fff9e6;font-weight:bold;color:#b45309">${tx.discount ? Number(tx.discount).toLocaleString('en-IN') : '-'}</td>` : ""}
//                           ${i === 0 ? `<td rowspan="${rowCount}" style="border:1px solid #ccc;padding:8px;">${pAmt}</td>` : ""}
//                           ${i === 0 ? `<td rowspan="${rowCount}" style="border:1px solid #ccc;padding:8px;">${tTotal}</td>` : ""}
//                           ${i === 0 ? `<td rowspan="${rowCount}" style="border:1px solid #ccc;padding:8px;font-weight:bold;">${pAmt + tTotal}</td>` : ""}
//                           ${i === 0 ? `<td rowspan="${rowCount}" style="border:1px solid #ccc;padding:8px;">${tx.transactionBy?.fullName || "-"}</td>` : ""}
//                         </tr>
//                       `;
//                     }).join("");
//                   }).join("")
//             }
//           </tbody>
//         </table>
//       </body>
//     </html>
//   `;
// };

//   const createPdfBlobFromHtml = async (htmlString) => {
//   const container = document.createElement("div");
//   container.style.position = "fixed";
//   container.style.left = "-9999px";
//   container.style.top = "0";
//   container.style.width = "800px"; // Constrain width explicitly for stable canvas dimensions
//   container.style.backgroundColor = "#ffffff";
  
//   const cleanHtml = htmlString
//     .replace(/<!DOCTYPE[^>]*>/i, "")
//     .replace(/<\/?html[^>]*>/gi, "")
//     .replace(/<\/?head[^>]*>/gi, "")
//     .replace(/<\/?body[^>]*>/gi, "");

//   container.innerHTML = cleanHtml;
//   document.body.appendChild(container);

//   // Force a slight paint execution gap to prevent blank snapshots
//   await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 150)));

//   try {
//     const worker = html2pdf()
//       .set({
//         margin: [10, 10, 10, 10],
//         filename: "report.pdf",
//         image: { type: "jpeg", quality: 0.98 },
//         html2canvas: {
//           scale: 2,
//           useCORS: true,
//           allowTaint: true,
//           logging: false,
//           backgroundColor: "#ffffff"
//         },
//         jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
//       })
//       .from(container);

//     // CRITICAL: Extract the raw PDF document blob using the worker promise chain 
//     // This blocks code advancement until the underlying array buffer has actual data
//     const pdfBlob = await worker.outputPdf("blob");
//     return pdfBlob;

//   } catch (error) {
//     console.error("PDF engine crash caught:", error);
//     throw error;
//   } finally {
//     if (document.body.contains(container)) {
//       document.body.removeChild(container);
//     }
//   }
// };

//   const handleGenerateReport = () => {
//     setReportDialogOpen(true);
//   };

//   const handleCloseReportDialog = () => {
//     setReportDialogOpen(false);
//     setReportDateRange({ startDate: null, endDate: null });
//   };

//   const downloadReport = async () => {
//     if (!reportDateRange.startDate || !reportDateRange.endDate) {
//       toast.error("Please select both start and end date");
//       return;
//     }

//     if (selectedCustomerIds.length === 0) {
//       toast.error("Select at least one customer first");
//       return;
//     }

//     setReportLoading(true);

//     try {
//       const payload = {
//         customerIds: selectedCustomerIds,
//         startDate: reportDateRange.startDate?.toISOString?.() || reportDateRange.startDate,
//         endDate: reportDateRange.endDate?.toISOString?.() || reportDateRange.endDate,
//       };

//       if (user?.roleId === 1) {
//         payload.employerId = selectedEmployerId;
//       }

//       const response = await axiosInstance.post(
//         "/customers/transactions-report",
//         payload
//       );

//       const customers = response?.data?.result || [];

//       if (customers.length === 0) {
//         toast.info("No customer transactions found for the selected range.");
//         return;
//       }

//       const zip = new JSZip();

//       for (const customer of customers) {
//         const html = createCustomerReportHtml(customer, customer.transactions || [], reportDateRange);
//         const pdfBlob = await createPdfBlobFromHtml(html);
//         zip.file(`${sanitizeFileName(customer.name)}.pdf`, pdfBlob);
//       }

//       const zipBlob = await zip.generateAsync({ type: "blob" });
//       const startLabel = reportDateRange.startDate?.format?.("YYYY-MM-DD") || new Date(reportDateRange.startDate).toISOString().slice(0, 10);
//       const endLabel = reportDateRange.endDate?.format?.("YYYY-MM-DD") || new Date(reportDateRange.endDate).toISOString().slice(0, 10);
//       const zipName = `Customer_Report_${startLabel}_${endLabel}.zip`;
//       const url = URL.createObjectURL(zipBlob);
//       const link = document.createElement("a");
//       link.href = url;
//       link.download = zipName;
//       document.body.appendChild(link);
//       link.click();
//       link.remove();
//       URL.revokeObjectURL(url);

//       toast.success("Report downloaded successfully");
//       handleCloseReportDialog();
//     } catch (err) {
//       toast.error(err?.response?.data?.message || err.message || "Failed to generate report");
//     } finally {
//       setReportLoading(false);
//     }
//   };

  // Open Edit Customer Popup
  const openEditCustomerPopup = (customer) => {
    setEditCustomer(true)
    setCustomerId(customer?._id)
    reset({
      name: customer?.name,
      mobile: customer?.mobile,
      location: customer?.location,
      branch: customer?.branchId
    })
    setOpen(true)
  }

  const getBranchName = branchId => {
    return branches.find(branch => branch?._id === branchId )?.name
  }

  const getEmployerName = () => {
    return paginatedCustomers?.length > 0 && paginatedCustomers[0]?.employerDetails?.employerName
  }

  const downloadSalesReport = async () => {
    if (!selectedDate) {
      toast.error("Please select a date to download the sales report.");
      return;
    }

    setReportLoading(true);
    try {
      const response = await axiosInstance.get("/pdf/sales-report", {
        params: {
          startDate: selectedDate,
          endDate: selectedDate,
          branchId: filteredBranch,
          employerId: selectedEmployerId,
          searchString: searchString,
        },
      });

      const transactions = response.data?.result || [];
      if (!transactions.length) {
        toast.info("No transactions found for the selected date.");
        return;
      }

      const targetDate = response.data?.reportDate || selectedDate;
      const reportLabel = dayjs(targetDate).isValid()
        ? dayjs(targetDate).format("DD MMM YYYY")
        : new Date(targetDate).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            timeZone: "Asia/Kolkata",
          });

      const val = (v) => v ?? "-";

      const grandTotalTransactionAmount = transactions.reduce((sum, tx) => {
        const salesTotal = (Array.isArray(tx.sales) ? tx.sales : []).reduce(
          (s, sale) => s + Number(sale?.total || 0),
          0
        );
        return sum + salesTotal;
      }, 0);

      const discountAmount = transactions.reduce((sum, tx) => sum + Number(tx?.discount || 0), 0);

      const damageRefund = transactions.reduce((sum, tx) => {
        const damageRows = Array.isArray(tx.damageRefunds)
          ? tx.damageRefunds
          : tx.damageRefund
          ? [tx.damageRefund]
          : [];
        return (
          sum +
          damageRows.reduce((s, damage) => s + Number(damage?.total || 0), 0)
        );
      }, 0);

      const labourCharges = transactions.reduce(
        (sum, tx) => sum + Number(tx?.labourCharges?.total || 0),
        0
      );

      const grandTotalPaidAmount = transactions.reduce(
        (sum, tx) => sum + Number(tx?.paymentAmount || 0),
        0
      );

      const grandTrayPaymentAmount = transactions.reduce((sum, tx) => {
        const trays = Array.isArray(tx.trays) ? tx.trays : [];
        return (
          sum +
          trays.reduce(
            (traySum, tray) =>
              traySum +
              Number(tray?.numberOfTrays || 0) * Number(tray?.costPerTray || 0),
            0
          )
        );
      }, 0);

      const grandTotalDue =
        grandTotalTransactionAmount +
        labourCharges -
        (grandTotalPaidAmount + grandTrayPaymentAmount + discountAmount + damageRefund);

      // Extract proprietor details safely out of transaction metadata context arrays if available
      const proprietorName = transactions[0]?.employerDetails?.proprietorName || user?.fullName || "-";
      const proprietorMobile = transactions[0]?.employerDetails?.mobile || user?.mobile || "-";

      const transactionRows = transactions
        .map((tx) => {
          const salesRows = Array.isArray(tx.sales) ? tx.sales : [];
          const damageRows = Array.isArray(tx.damageRefunds)
            ? tx.damageRefunds.map((dr) => ({ ...dr, isDamage: true }))
            : [];
          const rows = [...salesRows];
          const trays = Array.isArray(tx.trays) ? tx.trays : [];
          const rowCount = Math.max(rows.length, trays.length, 1);
          const payment = Number(tx.paymentAmount || 0);
          const tTotal = trays.reduce(
            (sum, tray) => sum + Number(tray.numberOfTrays || 0) * Number(tray.costPerTray || 0),
            0
          );

          return Array.from({ length: rowCount })
            .map((_, index) => {
              const sale = rows[index];
              const tray = trays[index];
              return `
                <tr>
                  ${index === 0 ? `<td rowspan="${rowCount}">${tx.customerName || "-"}</td>` : ""}
                  <td class="${sale?.isDamage ? "damage-text" : ""}">${val(sale?.itemName)}</td>
                  <td class="${sale?.isDamage ? "damage-text" : ""}">${val(sale?.numberOfItems)}</td>
                  <td class="${sale?.isDamage ? "damage-text" : ""}">${val(sale?.costPerItem != null ? Math.round(Number(sale.costPerItem)) : sale?.costPerItem)}</td>
                  <td class="${sale?.isDamage ? "damage-text" : ""}">${sale?.isDamage ? "-" : ""}${val(sale?.total != null ? Math.round(Number(sale.total)) : sale?.total)}</td>
                  <td class="tray-column">${val(tray?.numberOfTrays)}</td>
                  <td class="tray-column">${val(tray?.costPerTray != null ? Math.round(Number(tray.costPerTray)) : tray?.costPerTray)}</td>
                  <td class="tray-column">${tray ? Math.round(Number(tray.numberOfTrays || 0) * Number(tray.costPerTray || 0)) : "-"}</td>
                  ${index === 0 ? `<td rowspan="${rowCount}">${Math.round(payment)}</td>` : ""}
                  ${index === 0 ? `<td rowspan="${rowCount}">${Math.round(tTotal)}</td>` : ""}
                  ${index === 0 ? `<td rowspan="${rowCount}" style="font-weight:bold">${Math.round(payment + tTotal)}</td>` : ""}
                  ${index === 0 ? `<td rowspan="${rowCount}">${tx.transactionBy?.fullName || tx.transactionBy || "-"}</td>` : ""}
                </tr>
              `;
            })
            .join("");
        })
        .join("");

      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8" />
          <title>Sales Report - ${reportLabel}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 30px; color: #333; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #1976d2; padding-bottom: 10px; margin-bottom: 20px; }
            .company-name { font-size: 24px; font-weight: bold; color: #1976d2; }
            .proprietor-meta { font-size: 13px; color: #555; margin-top: 4px; }
            .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 20px; background: #f5f5f5; padding: 15px; border-radius: 8px; }
            .summary-item { text-align: center; }
            .summary-label { font-size: 10px; color: #666; text-transform: uppercase; }
            .summary-value { font-size: 14px; font-weight: bold; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: center; vertical-align: middle; }
            th { background: #f0f0f0; font-weight: bold; }
            .tray-column { background-color: #e4ffe4 !important; -webkit-print-color-adjust: exact; }
            .damage-text { background-color: #d32f2f; color: white !important; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; border-top: 1px solid #ccc; padding-top: 10px; display: flex; justify-content: space-between; }
            @media print { body { margin: 10px; } tr { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="header">
            <div style="display: flex; align-items: center; gap: 12px;">
              <img src="${LOGO_BASE64}" style="width: 50px; height: 50px; border-radius: 4px;" />
              <div>
                <div class="company-name">${getEmployerName() || ""}</div>
                <div style="color: #666; font-size: 16px; font-weight: 500;">${getBranchName(filteredBranch) || "Sales Report"}</div>
                <div class="proprietor-meta">
                  Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
                </div>
              </div>
            </div>
            <div style="text-align: right;">
              <div style="font-size: 20px; font-weight: bold; text-transform: uppercase;">Sales Report</div>
              <div style="font-size: 12px; color: #666; margin-top: 4px;">Report Date: ${reportLabel}</div>
            </div>
          </div>

          <div class="summary-grid">
            <div class="summary-item"><div class="summary-label">Total Sales</div><div class="summary-value">₹${Math.round(grandTotalTransactionAmount || 0).toLocaleString("en-IN")}</div></div>
            <div class="summary-item"><div class="summary-label">Total Tray Payment</div><div class="summary-value" style="color:green">₹${Math.round(grandTrayPaymentAmount || 0).toLocaleString("en-IN")}</div></div>
            <div class="summary-item"><div class="summary-label">Total Cash/Online Payment</div><div class="summary-value" style="color:green">₹${Math.round(grandTotalPaidAmount || 0).toLocaleString("en-IN")}</div></div>
          </div>

          <table>
            <thead>
              <tr>
                <th rowspan="2">Customer Name</th>
                <th colspan="4">Sales</th>
                <th colspan="3" class="tray-column">Trays Returned</th>
                <th colspan="3">Payment</th>
                <th rowspan="2">By</th>
              </tr>
              <tr>
                <th>Item</th><th>Qty</th><th>Price</th><th>Total</th>
                <th class="tray-column">Qty</th><th class="tray-column">Price</th><th class="tray-column">Total</th>
                <th>Cash</th><th>Tray</th><th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${transactionRows}
            </tbody>
          </table>

          <div class="footer">
            <span>Total Transactions: ${transactions.length}</span>
            <span>Generated on: ${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to load sales report");
    } finally {
      setReportLoading(false);
    }
  };

  const printPDF = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
      <meta charset="UTF-8" />
      <title>Due Report - ${`${dayjs(selectedDate).format("DD MMM YYYY")}`}</title>

      <style>
        body {
          font-family: Arial, Helvetica, sans-serif;
          margin: 4px;
          color: #000;
        }

        h2 {
          margin: 0;
          text-transform: capitalise;
        }

        .header {
          border-bottom: 2px solid #000;
          padding-bottom: 8px;
          margin-bottom: 12px;
        }

        .meta {
          font-size: 12px;
          margin-top: 4px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
          margin-top: 12px;
        }

        th, td {
          border: 1px solid #000;
          padding: 6px;
          vertical-align: middle;
        }

        thead th {
          background: #eee;
          text-align: center;
          font-weight: bold;
        }

        .right {
          text-align: right;
        }

        tr {
          page-break-inside: avoid;
        }

        .footer {
          margin-top: 14px;
          font-size: 10px;
          border-top: 1px solid #000;
          padding-top: 6px;
          display: flex;
          justify-content: space-between;
        }

        .proprietor-meta { font-size: 13px; color: #555; margin-top: 4px; }

        @page {
          size: A5 portrait;   /* or A5 landscape */
          margin: 10mm;
        }
      </style>
      </head>

      <body>
        <div style="display: flex; align-items: center; gap: 12px">
          <img
            src=${LOGO_BASE64}
            alt="DBF Logo"
            style="width: 50px; height: 50px; border-radius: 4px"
          />
          <div>
            <div
              style="font-size: 20px; font-weight: bold; color: #1976d2;">
              ${getEmployerName()}
            </div>
            <div style="color: #666; font-size: 16px">
              ${getBranchName(filteredBranch)}
            </div>
            <div class="proprietor-meta">
              Proprietor: <strong>Maddireddy Ramesh</strong> &bull; Contact: <strong>+91 76666 77779</strong>
            </div>
          </div>
        </div>

      <div class="header">
        <h2>Customers Due Report</h2>
        <div class="meta">
          Generated on: ${new Date().toLocaleString("en-IN")}
        </div>
      </div>

      <table>
      <thead>
      <tr>
        <th style="width: 45%">Customer Name</th>
        <th style="width: 35%">Location</th>
        <th style="width: 20%">Due Amount (₹)</th>
      </tr>
      </thead>

      <tbody>
      ${customersList
        .map(
          (c) => `
      <tr>
        <td>${c.name || "-"}</td>
        <td>${c.location || "-"}</td>
        <td class="right">
          ${Math.round(Number(c.totalDueAmount || 0)).toLocaleString("en-IN")}
        </td>
      </tr>`
        )
        .join("")}
      </tbody>
      </table>

      <div class="footer">
        <span>Total Customers: ${customersList.length}</span>
        <span>Total Due Amount: ${Math.round(customersList.reduce((acc, el)=>acc + (Number(el.totalDueAmount) || 0), 0)).toLocaleString("en-IN")}</span>
      </div>

      </body>
      </html>
    `);
     printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {isDeletedPage ? "Deleted Customers" : "Customers"}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, justifyContent: "flex-end" }}>
          <TextField
            sx={{ minWidth: 220 }}
            placeholder="Search by Name/Mobile"
            name="search"
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />

          {
            user?.roleId === 1 && 
            <TextField
              sx={{ minWidth: 160 }}
              select
              label="Employer"
              size="small"
              value={selectedEmployerId}
              onChange={(e) => setSelectedEmployerId(e.target.value)}
            >
              {employers.map((employer) => (
                <MenuItem key={employer._id} value={employer._id}>
                  {employer.employerName}
                </MenuItem>
              ))}
            </TextField>
          }

          {
            branches.length > 0 && 
            <TextField
              sx={{ minWidth: 160 }}
              select
              label="Branch"
              size="small"
              value={filteredBranch}
              onChange={(e) => handleBranchChange(e.target.value)}
            >
              {branches.map((branch) => (
                <MenuItem key={branch._id} value={branch._id}>
                  {branch.name}
                </MenuItem>
              ))}
            </TextField>
          }
          {!isDeletedPage && (
            <TextField
              sx={{ minWidth: 170 }}
              label="As of date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                max: dayjs().format("YYYY-MM-DD")
              }}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          )}
          {!isDeletedPage && (
            <Button
              size="small"
              onClick={() => setStatusFilter(prev => prev === 'inactive' ? 'all' : 'inactive')}
              sx={{
                borderRadius: '20px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                py: '6px',
                fontSize: '0.85rem',
                border: '1px solid',
                borderColor: statusFilter === 'inactive' ? 'rgba(239, 68, 68, 0.3)' : 'divider',
                backgroundColor: statusFilter === 'inactive' ? 'rgba(239, 68, 68, 0.08)' : 'background.paper',
                color: statusFilter === 'inactive' ? '#b91c1c' : 'text.primary',
                boxShadow: statusFilter === 'inactive' ? '0 4px 12px rgba(239, 68, 68, 0.12)' : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                height: '40px',
                '&:hover': {
                  backgroundColor: statusFilter === 'inactive' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(0, 0, 0, 0.02)',
                  borderColor: statusFilter === 'inactive' ? 'rgba(239, 68, 68, 0.5)' : 'text.secondary',
                  transform: 'translateY(-1px)',
                  boxShadow: statusFilter === 'inactive' ? '0 6px 16px rgba(239, 68, 68, 0.18)' : '0 2px 8px rgba(0,0,0,0.05)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
              startIcon={
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusFilter === 'inactive' ? '#ef4444' : '#94a3b8',
                  animation: statusFilter === 'inactive' ? 'pulse 2s infinite' : 'none',
                  transition: 'all 0.3s',
                  ...pulseAnimation
                }} />
              }
            >
              Inactive Customers
            </Button>
          )}
          {
            user?.roleId !== 1 && !isDeletedPage &&
            <Tooltip title="Print Due Report" arrow>
              <span>
                <IconButton
                  color="primary"
                  onClick={printPDF}
                  disabled={!selectedDate || customersList.length === 0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    p: '8px',
                    backgroundColor: 'background.paper',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.12)',
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    }
                  }}
                >
                  <PrintIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          }
          {!isDeletedPage && (
            <Tooltip title={reportLoading ? "Downloading..." : "Sales Report"} arrow>
              <span>
                <IconButton
                  color="primary"
                  onClick={downloadSalesReport}
                  disabled={!selectedDate || reportLoading || customersList.length === 0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    p: '8px',
                    backgroundColor: 'background.paper',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.12)',
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    }
                  }}
                >
                  {reportLoading ? <CircularProgress size={20} /> : <AssessmentIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!isDeletedPage && (
            <Tooltip title="Add Customer" arrow>
              <IconButton
                onClick={() => setOpen(true)}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  borderRadius: '12px',
                  p: '9px',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)',
                    backgroundColor: 'primary.dark',
                  }
                }}
              >
                <PersonAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Loading State */}
      {loading && customersList.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" my={4}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading customers...
          </Typography>
        </Box>
      ) : (
        <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 'none', backgroundColor: 'transparent', position: 'relative' }}>
          {loading && (
            <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 10 }}>
              <LinearProgress />
            </Box>
          )}
          <TableContainer>
            <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 8px', minWidth: 650 }}>
              <TableHead>
                <TableRow sx={{ '& th': { border: 'none', backgroundColor: 'transparent', color: 'text.secondary', py: 1 } }}>
                  {/* <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                      width: 48,
                    }}
                    padding="checkbox"
                  >
                    <Checkbox
                      checked={isAllPageSelected}
                      indeterminate={
                        selectedCustomerIds.length > 0 &&
                        !isAllPageSelected &&
                        paginatedCustomers.some((customer) =>
                          selectedCustomerIds.includes(customer._id)
                        )
                      }
                      onChange={handleSelectAllCustomers}
                      inputProps={{
                        "aria-label": "select all customers",
                      }}
                    />
                  </TableCell> */}
                  <TableCell
                    sx={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <b>Name</b>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <b>Mobile No.</b>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <b>Location</b>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <b>Total Amount Due</b>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "transparent",
                    }}
                  >
                    <b>Actions</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCustomers.length > 0 ? (
                  paginatedCustomers.map((customer) => {
                    const referenceDate = selectedDate ? dayjs(selectedDate) : dayjs();
                    const isInactive = customer.lastTransactionDate
                      ? referenceDate.diff(dayjs(customer.lastTransactionDate), 'day') > 30
                      : true;
                    return (
                      <TableRow
                        hover
                        key={customer._id}
                        sx={{
                          backgroundColor: isInactive ? "rgba(239, 68, 68, 0.01)" : "rgba(16, 185, 129, 0.005)",
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.01)',
                          '& td': {
                            borderTop: '1px solid',
                            borderBottom: '1px solid',
                            borderColor: isInactive ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                          },
                          '& td:first-of-type': {
                            borderLeft: '1px solid',
                            borderTopLeftRadius: '12px',
                            borderBottomLeftRadius: '12px',
                            borderColor: isInactive ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                            borderLeftColor: isInactive ? '#ef4444 !important' : '#10b981 !important',
                            borderLeftWidth: '4px !important',
                          },
                          '& td:last-of-type': {
                            borderRight: '1px solid',
                            borderTopRightRadius: '12px',
                            borderBottomRightRadius: '12px',
                            borderColor: isInactive ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)',
                          },
                          "&:hover": {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.05)',
                            backgroundColor: isInactive ? "rgba(239, 68, 68, 0.03) !important" : "rgba(16, 185, 129, 0.015) !important",
                            '& td': {
                              borderColor: isInactive ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                            }
                          }
                        }}
                      >
                        {/* <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedCustomerIds.includes(customer._id)}
                            onChange={(e) => handleSelectCustomer(e, customer._id)}
                            inputProps={{
                              "aria-label": `select customer ${customer.name}`,
                            }}
                          />
                        </TableCell> */}
                        <TableCell sx={{ py: 1.5 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle2" fontWeight="600" sx={{ color: isInactive ? 'text.secondary' : 'text.primary' }}>
                              {customer.name}
                            </Typography>
                            {isInactive && statusFilter === 'inactive' && (
                              <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                mt: 0.75,
                                px: 1.25,
                                py: 0.25,
                                borderRadius: '12px',
                                backgroundColor: 'rgba(239, 68, 68, 0.06)',
                                border: '1px solid rgba(239, 68, 68, 0.15)',
                                width: 'fit-content',
                              }}>
                                <Box sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  backgroundColor: '#ef4444',
                                  animation: 'pulse 1.5s infinite',
                                  ...pulseAnimation
                                }} />
                                <Typography variant="caption" sx={{ color: '#b91c1c', fontWeight: 600, fontSize: '0.725rem' }}>
                                  Inactive for {referenceDate.diff(dayjs(customer.lastTransactionDate), 'day')} days
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", py: 1 }}>
                        +91 {customer.mobile}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                        }}
                      >
                        {customer.location}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                        }}
                      >
                        {`₹ ${Math.round(Number(
                          customer.totalDueAmount || 0
                        )).toLocaleString("en-IN")}`}
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 1,
                        }}
                      >
                        {statusFilter === "deleted" ? (
                          <>
                            {
                              user?.roleId !== 1 && (
                                <Tooltip title="View Details" arrow placement="top">
                                  <IconButton
                                    color="primary"
                                    onClick={() =>
                                      navigate(`/customer-details/${customer._id}`)
                                    }
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                              )
                            }
                            {
                              [3].indexOf(user?.roleId) <= -1 && (
                                <Tooltip title="Restore Customer" arrow placement="top">
                                  <IconButton
                                    color="success"
                                    onClick={() => handleRestoreCustomer(customer._id)}
                                  >
                                    <RestoreIcon />
                                  </IconButton>
                                </Tooltip>
                              )
                            }
                          </>
                        ) : (
                          <>
                            {
                              user?.roleId !== 1 &&
                              <Tooltip title="View Details" arrow placement="top">
                                <IconButton
                                  color="primary"
                                  onClick={() =>
                                    navigate(`/customer-details/${customer._id}`)
                                  }
                                >
                                  <VisibilityIcon />
                                </IconButton>
                              </Tooltip>
                            }
                            {
                              [3].indexOf(user?.roleId) <= -1 && <Tooltip title="Edit" arrow placement="top">
                              <IconButton
                                color="primary"
                                onClick={()=>openEditCustomerPopup(customer)}
                              >
                                <FaEdit size={19} />
                              </IconButton>
                            </Tooltip>
                            }
                            {
                              user?.roleId === 1 && (
                                <Tooltip title="Update Tray Balance">
                                  <IconButton
                                    color="secondary"
                                    onClick={() => openTrayPopup(customer)}
                                  >
                                    <Inventory2Icon />
                                  </IconButton>
                                </Tooltip>
                              )
                            }
                            {
                              [3].indexOf(user?.roleId) <= -1 && (
                                <Tooltip title="Delete Customer" arrow placement="top">
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDeleteCustomer(customer)}
                                  >
                                    <FaTrash size={19} />
                                  </IconButton>
                                </Tooltip>
                              )
                            }
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );})
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ py: 3, color: "text.secondary" }}
                    >
                      <Typography
                        color="text.secondary"
                        variant="h6"
                        fontWeight="bold"
                      >
                        No customers found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={customersList.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </TableContainer>
        </Paper>
      )}
{/* 
      <Dialog
        open={reportDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleCloseReportDialog();
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          variant="h6"
          fontWeight="bold"
          sx={{ background: "#f5f5f5" }}
        >
          Generate Report
        </DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <Typography>Selected customers: {selectedCustomerIds.length}</Typography>
            <RangeFilter
              dateRange={reportDateRange}
              setDateRange={setReportDateRange}
              width={200}
              ranges={reportRanges}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReportDialog} disabled={reportLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={downloadReport}
            disabled={!reportDateRange.startDate || !reportDateRange.endDate || reportLoading}
          >
            {reportLoading ? "Generating..." : "Download Report"}
          </Button>
        </DialogActions>
      </Dialog> */}

      {/* Dialog for Add Customer */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleCreateCustomerPopupClose();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          variant="h6"
          fontWeight="bold"
          sx={{
            background: "#f5f5f5",
          }}
        >
          {editCustomer ? 'Edit Customer' : 'Add Customer'}
        </DialogTitle>
        <form onSubmit={handleSubmit(handleCreateCustomerSubmit)} noValidate>
          <DialogContent>
            <Box display="grid" gap={2} mt={1}>
              <TextField
                label="Name"
                name="name"
                fullWidth
                required
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
              />

              <TextField
                label="Mobile Number"
                name="mobile"
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">🇮🇳 +91</InputAdornment>
                  ),
                  inputMode: "numeric",
                }}
                {...register("mobile")}
                error={!!errors.mobile}
                helperText={errors.mobile?.message}
              />

              <TextField
                label="Location"
                name="location"
                fullWidth
                required
                {...register("location")}
                error={!!errors.location}
                helperText={errors.location?.message}
              />

              <FormControl
                sx={{ flex: 1 }}
                error={!!errors?.branch}
              >
                <InputLabel>Branch</InputLabel>

                <Controller
                  name={`branch`}
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      label="Branch"
                      value={field.value ?? ""}
                      disabled={editCustomer}
                      onChange={(e) =>
                        field.onChange(e.target.value)
                      }
                    >
                      {branches.map((branch) => (
                        <MenuItem key={branch._id} value={branch._id}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />

                <FormHelperText>
                  {errors?.branch?.message}
                </FormHelperText>
              </FormControl>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCreateCustomerPopupClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Submitting" : "Submit"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={openTrayDialog}
        onClose={() => setOpenTrayDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{ background: "#f5f5f5", fontWeight: "bold" }}
        >
          Update Tray Balance
        </DialogTitle>

        <DialogContent>
          <Box display="grid" gap={2} mt={1}>

            <FormControl fullWidth>
              <InputLabel>Tray Type</InputLabel>
              <Select
                value={trayTypeId || ""}
                label="Tray Type"
                onChange={(e) => setTrayTypeId(Number(e.target.value))}
              >
                {trayTypes.map((tray) => (
                    tray?.status && 
                    <MenuItem key={tray.trayTypeId} value={tray.trayTypeId}>
                      {tray.name + '(₹'+tray.price+')'}
                    </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Tray Count"
              type="number"
              fullWidth
              value={trayCount}
              onChange={(e) => setTrayCount(e.target.value)}
            />

          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenTrayDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleUpdateTray}
            disabled={savingTray}
          >
            {savingTray ? "Saving..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={openDeleteDialog}
        onClose={cancelDeleteCustomer}
        onConfirm={confirmDeleteCustomer}
        title="Delete Customer"
        message="Are you sure you want to delete this customer? You can restore them later from the Deleted Customers list."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
}

export default ListPage;